import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Container } from '@vnetwork/ui/layout';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Checkbox,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { StatusPill } from '@vnetwork/ui/data';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { usePromo, useUpdatePromo, pgTimestampToIso, type PromoStatus } from '@/api/promos';
import { DetailHeader } from '@/components/detail-header';
import { InfoGrid, type InfoItem } from '@/pages/account/components/InfoGrid';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { UsageHistoryTab } from './usage-history-tab';
import { formatDate, formatDateTime, formatMoney } from '@/lib/format';
import { DateInput } from '@/components/date-input';
import { NumericInput } from '@/components/numeric-input';

/* ── helpers ───────────────────────────────────────────────────────────── */

function statusTone(s: string): 'neutral' | 'success' | 'danger' {
  if (s === 'active') return 'success';
  if (s === 'expired') return 'danger';
  return 'neutral';
}

function humanize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatDiscount(promo: {
  promo_type: string;
  discount_value: number;
  currency?: string;
}): string {
  if (promo.promo_type === 'percentage') return `${promo.discount_value}%`;
  if (promo.promo_type === 'fixed_amount') return formatMoney(promo.discount_value);
  return String(promo.discount_value);
}

/** Build RFC3339 from a YYYY-MM-DD input value (midnight local, timezone-safe). */
function dateInputToRfc3339(val: string): string | undefined {
  if (!val) return undefined;
  const [y, m, d] = val.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  const dt = new Date(y, m - 1, d, 0, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  const tzOffset = -dt.getTimezoneOffset();
  const sign = tzOffset >= 0 ? '+' : '-';
  const absOff = Math.abs(tzOffset);
  return `${y}-${pad(m)}-${pad(d)}T00:00:00${sign}${pad(Math.floor(absOff / 60))}:${pad(absOff % 60)}`;
}

/** Extract YYYY-MM-DD from a pgtype.Timestamp or ISO string for date inputs. */
function isoToDateInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/* ── constants ─────────────────────────────────────────────────────────── */

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Expired', value: 'expired' },
];

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'usage-history', label: 'Usage history' },
] as const;

const TAB_VALUES = TABS.map((t) => t.value) as readonly string[];

/* ── Overview + edit tab ───────────────────────────────────────────────── */

interface EditState {
  name: string;
  description: string;
  discount_value: string;
  max_discount: string;
  max_uses: string;
  max_uses_per_customer: string;
  valid_from: string;
  valid_until: string;
  min_order_amount: string;
  new_customers_only: boolean;
  status: PromoStatus;
}

function OverviewTab({
  promoId,
  onUpdated,
}: {
  promoId: number;
  onUpdated: () => void;
}) {
  const toast = useToast();
  const { data, isLoading, isError, refetch } = usePromo(promoId);
  const update = useUpdatePromo(promoId);

  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState('');
  const [form, setForm] = useState<EditState | null>(null);

  if (isLoading) return <TabLoading rows={6} />;
  if (isError)
    return <TabError title="Failed to load promo details" onRetry={() => refetch()} />;

  const promo = data?.result;
  if (!promo) return null;

  const validFrom = pgTimestampToIso(promo.valid_from);
  const validUntil = pgTimestampToIso(promo.valid_until);
  const createdAt = pgTimestampToIso(promo.created_at);
  const updatedAt = pgTimestampToIso(promo.updated_at);

  const startEdit = () => {
    setForm({
      name: promo.name,
      description: promo.description ?? '',
      discount_value: String(promo.discount_value),
      max_discount: promo.max_discount != null ? String(promo.max_discount) : '',
      max_uses: promo.max_uses != null ? String(promo.max_uses) : '',
      max_uses_per_customer: String(promo.max_uses_per_customer ?? 1),
      valid_from: isoToDateInput(validFrom),
      valid_until: isoToDateInput(validUntil),
      min_order_amount:
        promo.min_order_amount != null ? String(promo.min_order_amount) : '',
      new_customers_only: promo.new_customers_only,
      status: promo.status,
    });
    setEditing(true);
    setEditError('');
  };

  const cancelEdit = () => {
    setEditing(false);
    setForm(null);
    setEditError('');
  };

  const saveEdit = async () => {
    if (!form) return;
    setEditError('');
    try {
      await update.mutateAsync({
        name: form.name.trim() || undefined,
        description: form.description.trim() || undefined,
        discount_value: form.discount_value ? Number(form.discount_value) : undefined,
        max_discount: form.max_discount ? Number(form.max_discount) : undefined,
        max_uses: form.max_uses ? Number(form.max_uses) : undefined,
        max_uses_per_customer: form.max_uses_per_customer
          ? Number(form.max_uses_per_customer)
          : undefined,
        valid_from: dateInputToRfc3339(form.valid_from),
        valid_until: dateInputToRfc3339(form.valid_until),
        min_order_amount: form.min_order_amount
          ? Number(form.min_order_amount)
          : undefined,
        new_customers_only: form.new_customers_only,
        status: form.status,
      });
      toast.success('Promo updated');
      setEditing(false);
      setForm(null);
      onUpdated();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Failed to update promo.';
      setEditError(msg);
    }
  };

  const set = (key: keyof EditState, value: string | boolean) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  /* ── read-only fact grid ── */
  const infoItems: InfoItem[] = [
    { label: 'Promo ID', value: promo.promo_id },
    { label: 'Code', value: <code style={{ fontFamily: 'monospace', fontWeight: 'var(--vn-font-weight-semibold)' }}>{promo.code}</code> },
    { label: 'Name', value: promo.name },
    { label: 'Description', value: promo.description || '—', full: true },
    {
      label: 'Type',
      value:
        promo.promo_type === 'percentage' ? 'Percentage'
        : promo.promo_type === 'fixed_amount' ? 'Fixed amount'
        : promo.promo_type === 'credit' ? 'Credit'
        : promo.promo_type,
    },
    { label: 'Discount value', value: formatDiscount(promo) },
    {
      label: 'Max discount cap',
      value: promo.max_discount != null ? formatMoney(promo.max_discount) : 'No cap',
    },
    { label: 'Currency', value: promo.currency || 'VND' },
    {
      label: 'Uses',
      value: `${promo.current_uses}${promo.max_uses != null ? ` / ${promo.max_uses}` : ' (unlimited)'}`,
    },
    {
      label: 'Max uses per customer',
      value: promo.max_uses_per_customer ?? 1,
    },
    {
      label: 'Min order amount',
      value:
        promo.min_order_amount != null ? formatMoney(promo.min_order_amount) : 'No minimum',
    },
    {
      label: 'New customers only',
      value: promo.new_customers_only ? 'Yes' : 'No',
    },
    { label: 'Valid from', value: validFrom ? formatDate(validFrom) : 'No start restriction' },
    { label: 'Valid until', value: validUntil ? formatDate(validUntil) : 'No expiry' },
    {
      label: 'Affiliate',
      value: promo.referrer_name ?? 'System',
    },
    { label: 'Created', value: formatDateTime(createdAt ?? undefined) },
    { label: 'Updated', value: formatDateTime(updatedAt ?? undefined) },
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {editError ? <Alert tone="danger" title={editError} /> : null}

      {/* Read-only overview */}
      <Card>
        <CardHeader>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <CardTitle>Promo details</CardTitle>
            {!editing ? (
              <Button variant="secondary" size="sm" onClick={startEdit}>
                Edit
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardBody>
          <InfoGrid items={infoItems} />
        </CardBody>
      </Card>

      {/* Edit form — shown in-place when editing */}
      {editing && form ? (
        <Card>
          <CardHeader>
            <CardTitle>Edit promo</CardTitle>
          </CardHeader>
          <CardBody>
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 'var(--vn-space-4)',
                }}
              >
                <Field label="Name" required>
                  {({ id }) => (
                    <Input
                      id={id}
                      value={form.name}
                      onChange={(e) => set('name', e.target.value)}
                    />
                  )}
                </Field>

                <Field label="Description" style={{ gridColumn: '1 / -1' }}>
                  {({ id }) => (
                    <Input
                      id={id}
                      value={form.description}
                      onChange={(e) => set('description', e.target.value)}
                    />
                  )}
                </Field>

                <Field label="Discount value">
                  {({ id }) => (
                    <NumericInput
                      id={id}
                      min={0}
                      value={form.discount_value}
                      onChange={(e) => set('discount_value', e.target.value)}
                    />
                  )}
                </Field>

                <Field label="Max discount cap" description="Blank = no cap">
                  {({ id }) => (
                    <NumericInput
                      id={id}
                      min={0}
                      value={form.max_discount}
                      onChange={(e) => set('max_discount', e.target.value)}
                    />
                  )}
                </Field>

                <Field label="Max uses" description="Blank = unlimited">
                  {({ id }) => (
                    <NumericInput
                      id={id}
                      min={1}
                      value={form.max_uses}
                      onChange={(e) => set('max_uses', e.target.value)}
                    />
                  )}
                </Field>

                <Field label="Max uses per customer">
                  {({ id }) => (
                    <NumericInput
                      id={id}
                      min={1}
                      value={form.max_uses_per_customer}
                      onChange={(e) => set('max_uses_per_customer', e.target.value)}
                    />
                  )}
                </Field>

                <Field label="Min order amount" description="Blank = no minimum">
                  {({ id }) => (
                    <NumericInput
                      id={id}
                      min={0}
                      value={form.min_order_amount}
                      onChange={(e) => set('min_order_amount', e.target.value)}
                    />
                  )}
                </Field>

                <Field label="Valid from">
                  {({ id }) => (
                    <DateInput
                      id={id}
                      value={form.valid_from}
                      onChange={(e) => set('valid_from', e.target.value)}
                    />
                  )}
                </Field>

                <Field label="Valid until" description="Blank = no expiry">
                  {({ id }) => (
                    <DateInput
                      id={id}
                      value={form.valid_until}
                      onChange={(e) => set('valid_until', e.target.value)}
                    />
                  )}
                </Field>

                <Field label="Status">
                  {({ id }) => (
                    <Select
                      value={form.status}
                      onValueChange={(v) => set('status', v as PromoStatus)}
                    >
                      <SelectTrigger id={id}>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>

                <Field label="New customers only" style={{ gridColumn: '1 / -1' }}>
                  {({ id }) => (
                    <label
                      htmlFor={id}
                      style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', cursor: 'pointer' }}
                    >
                      <Checkbox
                        id={id}
                        checked={form.new_customers_only}
                        onCheckedChange={(v) => set('new_customers_only', v === true)}
                      />
                      <span
                        style={{
                          fontSize: 'var(--vn-font-size-sm)',
                          color: 'var(--vn-fg-default)',
                        }}
                      >
                        Restrict to new customers only
                      </span>
                    </label>
                  )}
                </Field>
              </div>

              <div style={{ display: 'flex', gap: 'var(--vn-space-2)', justifyContent: 'flex-end' }}>
                <Button variant="ghost" type="button" onClick={cancelEdit}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="button"
                  loading={update.isPending}
                  onClick={saveEdit}
                >
                  Save changes
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}

/* ── Detail page ────────────────────────────────────────────────────────── */

/**
 * `/promos/:id` — Promo detail page with 2 tabs: Overview (editable facts)
 * and Usage history (paginated redemption records). Mirrors the
 * AffiliateDetailPage header + tab pattern.
 */
export default function PromoDetailPage() {
  const { id: idParam } = useParams();
  const id = Number(idParam) || 0;
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = searchParams.get('tab');
  const activeTab =
    requestedTab && TAB_VALUES.includes(requestedTab) ? requestedTab : 'overview';

  // Fetch header data only (lightweight)
  const { data, isLoading, isError, refetch } = usePromo(id);
  const promo = data?.result;

  const onTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <Container size="full">
      {isError ? (
        <TabError title="Failed to load promo" onRetry={() => refetch()} />
      ) : isLoading || !promo ? (
        <TabLoading rows={4} />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <DetailHeader
            parent={{ label: 'Promos', to: '/promos' }}
            current={promo.code}
            idPrefix={`#${promo.promo_id}`}
            title={promo.name}
            meta={
              <StatusPill tone={statusTone(promo.status)}>
                {humanize(promo.status)}
              </StatusPill>
            }
          />

          <Tabs value={activeTab} onValueChange={onTabChange}>
            <TabsList>
              {TABS.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="overview">
              <OverviewTab promoId={id} onUpdated={() => refetch()} />
            </TabsContent>

            <TabsContent value="usage-history">
              {activeTab === 'usage-history' ? (
                <UsageHistoryTab promoId={id} />
              ) : null}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </Container>
  );
}
